import { Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { App } from '@capacitor/app';
import { UsersService } from './users.service';
import { MessagePopupComponent } from '../modals/message-popup/message-popup.component';

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService {
  currentVersion: string = '';
  latestVersion: string = '';

  constructor(
    private platform: Platform,
    private usersService: UsersService,
    private modalController: ModalController
  ) {}

  /**
   * Get the current app version using Capacitor
   */
  private async getCurrentVersion(): Promise<string> {
    try {
      const info = await App.getInfo();
      return info.version;
    } catch (error) {
      console.error('Error getting app version:', error);
      return '0.0.1'; // Fallback version
    }
  }

  /**
   * Compare two version strings
   * Returns:
   * - 0 if versions are equal
   * - -1 if current version is lower than latest version (update needed)
   * - 1 if current version is higher than latest version
   */
  private compareVersions(
    currentVersion: string,
    latestVersion: string
  ): number {
    const current = this.parseVersion(currentVersion);
    const latest = this.parseVersion(latestVersion);

    for (let i = 0; i < Math.max(current.length, latest.length); i++) {
      const currentPart = current[i] || 0;
      const latestPart = latest[i] || 0;

      if (currentPart < latestPart) {
        return -1; // Update needed
      } else if (currentPart > latestPart) {
        return 1; // Current version is higher
      }
    }

    return 0; // Versions are equal
  }

  /**
   * Parse version string into numeric array
   * e.g., "1.2.3" -> [1, 2, 3]
   */
  private parseVersion(version: string): number[] {
    return version.split('.').map((part) => parseInt(part, 10));
  }

  /**
   * Check if update is needed
   */
  private isUpdateNeeded(
    currentVersion: string,
    latestVersion: string
  ): boolean {
    return this.compareVersions(currentVersion, latestVersion) === -1;
  }

  /**
   * Show the update modal
   */
  private async showUpdateModal(
    currentVersion: string,
    latestVersion: string
  ): Promise<void> {
    let modal = await this.modalController.create({
      component: MessagePopupComponent,
      componentProps: {
        title: 'update-required',
        text: 'update-description',
        buttonText: 'update-now',
        buttonFunction: () => this.updateApp(),
      },
      cssClass: 'alert-modal',
    });

    await modal.present();
  }

  /**
   * Perform the version check and show update modal if needed
   */
  async checkForUpdates(): Promise<void> {
    try {
      // Get latest version from API
      const currentVersion = await this.getCurrentVersion();

      this.usersService.getLatestVersion().subscribe(
        async (response: any) => {
          console.log('Version check response:', response);

          const latestVersion = response.version;

          if (!latestVersion) {
            console.warn('No version information received from API');
            return;
          }

          // Check if update is needed (current version < latest version)
          if (this.isUpdateNeeded(currentVersion, latestVersion.toString())) {
            // Show update modal
            await this.showUpdateModal(
              currentVersion,
              latestVersion.toString()
            );
          } else {
            console.log('App is up to date');
          }
        },
        (error) => {
          console.error('Error checking for updates:', error);
        }
      );
    } catch (error) {
      console.error('Error in version check:', error);
    }
  }

  async updateApp() {
    try {
      let storeUrl: string;

      // Better platform detection using multiple checks
      const isCapacitor = this.platform.is('capacitor');
      const isAndroid = this.platform.is('android') && isCapacitor;
      const isIOS = this.platform.is('ios') && isCapacitor;
      const isWeb = !isCapacitor;

      console.log('Platform detection:', {
        isCapacitor,
        isAndroid,
        isIOS,
        isWeb,
        platforms: this.platform.platforms(),
      });

      if (isAndroid) {
        console.log('Android Native');
        // For Android, open Play Store
        const packageId = await this.getPackageId();

        // Use market:// protocol to open Play Store app, fallback to web
        const marketUrl = `market://details?id=${packageId}`;
        const webUrl = `https://play.google.com/store/apps/details?id=${packageId}`;

        try {
          // Try to open Play Store app first
          window.location.href = marketUrl;
          return;
        } catch (error) {
          storeUrl = webUrl;
        }
      } else if (isIOS) {
        console.log('iOS Native');
        // For iOS, open App Store
        const packageId = await this.getPackageId();
        storeUrl = `https://apps.apple.com/app/id${packageId}`;
      } else {
        console.log('Web/Browser');
        // For web/browser environments
        storeUrl = 'https://teveni.app/home';
      }

      // Open the store URL
      if (storeUrl) {
        window.open(storeUrl, '_blank');
      }
    } catch (error) {
      console.error('Error opening store:', error);
    }
  }

  private async getPackageId(): Promise<string> {
    try {
      const info = await App.getInfo();
      return info.id;
    } catch (error) {
      console.error('Error getting package ID:', error);
      return '';
    }
  }
}
